import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiBell } from 'react-icons/fi';
import { notificationService } from '../../services/api';
import NotificationPanel from './NotificationPanel';

// The container is only the positioning anchor for the dropdown — the clickable
// surface is the button below, so it must not advertise a cursor of its own.
const IconContainer = styled.div`
  position: relative;
  margin-right: ${({ theme }) => theme.spacing[3]};
`;

// A real <button> that CONTAINS the badge, so bell + count are one target
// whether or not a count is shown.
const Icon = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.2s;

  /* globalStyles paints every bare <button> as the primary action, including an
     inset press shadow on hover — undo that for this icon button. */
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-act);
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.error};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  /* The badge is decoration on top of the button; it must never eat the click. */
  pointer-events: none;
`;

const NotificationIcon = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  // The bell itself, so the outside-click handler can ignore it — otherwise
  // mousedown closes the panel and the following click immediately reopens it,
  // making the button's aria-expanded toggle impossible to collapse by mouse.
  const buttonRef = useRef(null);
  // Track whether the first load has completed so the 60s background poll
  // doesn't blank an open panel to 'Loading...' every cycle.
  const hasLoadedRef = useRef(false);
  
  useEffect(() => {
    fetchNotifications();
    
    // Set up polling for new notifications (every 1 minute)
    const interval = setInterval(fetchNotifications, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    // Add click event listener to close panel when clicking outside
    const handleClickOutside = (event) => {
      if (buttonRef.current && buttonRef.current.contains(event.target)) return; // let the bell toggle
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [panelRef]);
  
  const fetchNotifications = async () => {
    try {
      // Only show the loading state on the very first fetch; background polls
      // refresh silently so an open panel isn't flashed to 'Loading...'.
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      const data = await notificationService.getNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount ?? 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  };
  
  const handleTogglePanel = () => {
    setIsOpen(!isOpen);
  };
  
  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => 
          notification.id === id ? { ...notification, read: true } : notification
        )
      );
      setUnreadCount(prevCount => Math.max(0, prevCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prevNotifications => 
        prevNotifications.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const handleDeleteNotification = async (id) => {
    try {
      await notificationService.deleteNotification(id);
      const updatedNotifications = notifications.filter(notification => notification.id !== id);
      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };
  
  return (
    <IconContainer>
      <Icon
        ref={buttonRef}
        type="button"
        onClick={handleTogglePanel}
        aria-label="Notifications"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <FiBell />
        {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
      </Icon>

      {isOpen && (
        <NotificationPanel
          ref={panelRef}
          notifications={notifications}
          loading={loading}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onDelete={handleDeleteNotification}
        />
      )}
    </IconContainer>
  );
};

export default NotificationIcon;